/**
 * The page "Master data" (15 Oct 2026): TanStack Query hooks over the three routes of the
 * master-data defects.
 *
 * - `GET /master-data/defects` (`listMasterDataDefects`): the defects of the caller's
 *   entities, most cases first, with the counts by kind. Every signed-in person (15 Oct
 *   2026; an executive-only session gets 403). A case id outside the caller's areas is
 *   left out and counted in `cases_hidden`.
 * - `GET /master-data/defects.csv` (`exportMasterDataDefects`): the same list as a CSV
 *   file. The page links to it; no generated call.
 * - `PATCH /master-data/defects/{id}` (`markMasterDataDefect`): "In progress", "Not a
 *   defect" (with a reason) or back to "Open". Every signed-in person, for the defects of
 *   the own entities. The server stores the role (manager, admin or sales), never the
 *   person.
 *
 * The defects are computed by a nightly check of the mail import, never typed in, and the
 * check is read only toward F&O. The first part of each query key is the request key of
 * the usage vocabulary (`master-data-defects`). The server keeps the list 60 seconds.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type ApiError } from "./client";
import {
  listMasterDataDefects,
  markMasterDataDefect,
  type MasterDataDefect,
  type MasterDataDefectList,
  type MasterDataMarkRequest,
} from "./generated";

export type {
  MasterDataDefect,
  MasterDataDefectList,
  MasterDataFact,
  MasterDataKindCount,
  MasterDataMarkRequest,
} from "./generated";

/** The closed lists of the API, read from the generated shapes. */
export type MasterDataKind = MasterDataDefect["kind"];
export type MasterDataStatus = MasterDataDefect["status"];
export type MasterDataMarkStatus = MasterDataMarkRequest["status"];
export type MasterDataRecordType = MasterDataDefect["record_type"];

/** The filters of the list and the CSV; a missing status means open and in progress. */
export type MasterDataQuery = {
  entity?: string;
  kind?: MasterDataKind;
  status?: MasterDataStatus;
  quiet?: boolean;
};

/** A reason of "Not a defect" has 1 to 300 characters. */
export const MASTER_DATA_REASON_MAX = 300;

/** The query keys; the first part is the request key of the usage vocabulary. */
export const masterDataKeys = {
  all: ["master-data-defects"] as const,
  list: (query: MasterDataQuery) => ["master-data-defects", query] as const,
};

/** The server keeps the list 60 seconds. */
const LIST_STALE_MS = 60_000;

/** The query of a request: only the filters that are set. */
function queryOf(query: MasterDataQuery): Record<string, string> {
  const out: Record<string, string> = {};
  if (query.entity) {
    out.entity = query.entity;
  }
  if (query.kind) {
    out.kind = query.kind;
  }
  if (query.status) {
    out.status = query.status;
  }
  if (query.quiet) {
    out.quiet = "true";
  }
  return out;
}

/** The defects of the caller's entities (every signed-in person). */
export function useMasterDataDefects(query: MasterDataQuery, options: { enabled: boolean }) {
  return useQuery({
    queryKey: masterDataKeys.list(query),
    queryFn: async (): Promise<MasterDataDefectList | null> => {
      const { data } = await listMasterDataDefects({
        query: {
          entity: query.entity,
          kind: query.kind,
          status: query.status,
          quiet: query.quiet ?? false,
        },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: LIST_STALE_MS,
  });
}

/** Mark one defect; the answer is the changed row. Every list is read again. */
export function useMarkMasterDataDefect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      defectId,
      request,
    }: {
      defectId: number;
      request: MasterDataMarkRequest;
    }): Promise<MasterDataDefect> => {
      const reason = typeof request.reason === "string" ? request.reason.trim() : "";
      if (request.status === "not_a_defect" && reason === "") {
        throw new RangeError("Write the reason first.");
      }
      if (reason.length > MASTER_DATA_REASON_MAX) {
        throw new RangeError(`At most ${MASTER_DATA_REASON_MAX} characters.`);
      }
      const { data } = await markMasterDataDefect({
        path: { defect_id: defectId },
        body: {
          status: request.status,
          reason: request.status === "not_a_defect" ? reason : null,
        },
        throwOnError: true,
      });
      return data as MasterDataDefect;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: masterDataKeys.all });
    },
  });
}

/** The CSV download of the filtered list (`GET /master-data/defects.csv`). */
export function masterDataExportHref(query: MasterDataQuery): string {
  const search = new URLSearchParams(queryOf(query)).toString();
  const path = "/api/master-data/defects.csv";
  return search === "" ? path : `${path}?${search}`;
}

/** What a failed master-data request reads as. Raw server text never reaches the screen. */
export function masterDataFailureText(error: unknown): string {
  if (error instanceof RangeError) {
    return error.message;
  }
  const { status } = (error ?? {}) as Partial<ApiError>;
  switch (status) {
    case 401:
      return "Your session has ended. Sign in again.";
    case 403:
      return "Only a manager or an admin can do this.";
    case 404:
      return "This record is not in your areas.";
    case 409:
      return "This record is fixed in F&O. Its status cannot change.";
    case 422:
      return "Write the reason first.";
    case 503:
      return "Master data is not available right now. Try again later.";
    default:
      return "This could not be saved. Try again in a moment.";
  }
}
